import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { getSearchDelay } from '@/lib/fake-player-generator';
import { getRandomFakeOpponent } from '@/lib/fake-leaderboard-generator';

export interface MatchmakingQueueEntry {
  id: string;
  user_id: string;
  time_control: string;
  elo_rating: number;
  joined_at: string;
  expires_at: string;
  status: 'waiting' | 'matched' | 'expired';
  game_session_id?: string | null;
}

export interface UserProfile {
  id: string;
  display_name: string | null;
  elo_rating: number;
  games_played: number;
  games_won: number;
  games_lost: number;
  games_drawn: number;
  preferred_time_control: string | null;
}

export function useMatchmaking() {
  const [isInQueue, setIsInQueue] = useState(false);
  const [queueEntry, setQueueEntry] = useState<MatchmakingQueueEntry | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [matchFound, setMatchFound] = useState<{
    game_session_id: string;
    time_control: string;
    player_color: 'white' | 'black';
    white_player_id: string;
    black_player_id: string;
    is_bot_game?: boolean;
    bot_elo?: number;
    fakeOpponent?: {
      username: string;
      elo: number;
    };
  } | null>(null);
  const [timeInQueue, setTimeInQueue] = useState(0);
  const [eloTolerance, setEloTolerance] = useState(100);
  const [queueStats, setQueueStats] = useState<{
    total_waiting: number;
    avg_wait_seconds: number;
    matches_last_hour: number;
    estimated_wait_seconds: number;
  } | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Ref to track if we're still searching (prevents stale closure bug)
  const isSearchingRef = useRef(false);
  
  // Ref to track auto-match timeout (for cleanup to prevent state updates on unmounted component)
  const autoMatchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Helper to clear the auto-match timeout
  const clearAutoMatchTimeout = useCallback(() => {
    if (autoMatchTimeoutRef.current) {
      clearTimeout(autoMatchTimeoutRef.current);
      autoMatchTimeoutRef.current = null;
    }
  }, []);

  // Ref to track if component is still mounted
  const isMountedRef = useRef(true);

  // Track mount/unmount to guard async state updates
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch user profile
  const fetchUserProfile = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (!isMountedRef.current) return;

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching user profile:', error);
      return;
    }

    if (!data) {
      // Create profile if it doesn't exist
      const { data: newProfile, error: createError } = await supabase
        .from('user_profiles')
        .insert({
          id: user.id,
          display_name: user.email?.split('@')[0] || 'Player'
        })
        .select()
        .single();

      if (!isMountedRef.current) return;

      if (createError) {
        console.error('Error creating user profile:', createError);
        return;
      }
      setUserProfile(newProfile);
    } else {
      setUserProfile(data);
    }
  }, [user]);

  // Join matchmaking queue
  const joinQueue = useCallback(async (timeControl: string) => {
    if (!user || !userProfile) {
      toast({
        title: "Authentication Required",
        description: "Please log in to join matchmaking",
        variant: "destructive"
      });
      return false;
    }

    try {
      setIsSearching(true);

      // Use secure RPC to find match or join queue with dynamic tolerance
      const { data, error } = await supabase.rpc('find_and_match_game_session', {
        _time_control: timeControl,
        _tolerance: eloTolerance
      });

      if (!isMountedRef.current) {
        return false;
      }

      if (error) throw error;

      const result = data?.[0] as any; // Cast to any since types haven't updated yet
      if (!result) throw new Error('No response from matchmaking service');

      if (result.match_status === 'match_found' && result.game_session_id) {
        setMatchFound({
          game_session_id: result.game_session_id,
          time_control: result.matched_time_control,
          player_color: result.player_color,
          white_player_id: result.white_player_id,
          black_player_id: result.black_player_id
        });
        setIsSearching(false);
        return {
          type: 'match_found',
          game_session_id: result.game_session_id,
          time_control: result.matched_time_control,
          player_color: result.player_color,
          white_player_id: result.white_player_id,
          black_player_id: result.black_player_id
        };
      } else if (result.match_status === 'joined_queue') {
        // Set searching state FIRST
        setIsInQueue(true);
        isSearchingRef.current = true;
        setTimeInQueue(0);

        // Set up auto-match timeout BEFORE querying queue data
        const delay = getSearchDelay();
        console.log(`⏰ Will auto-match with fake player in ${delay / 1000}s if no real opponent found`);
        
        // Clear any existing timeout first
        clearAutoMatchTimeout();
        
        // Store the timeout ID so we can cancel it
        autoMatchTimeoutRef.current = setTimeout(() => {
          console.log('⏰ Auto-match timeout fired:', {
            isMounted: isMountedRef.current,
            isSearching: isSearchingRef.current,
            delay: delay / 1000
          });
          
          // Guard against unmounted updates
          if (!isMountedRef.current) {
            console.log('❌ Component unmounted, skipping auto-match');
            return;
          }

          // Check if still searching using ref (prevents stale closure bug)
          if (!isSearchingRef.current) {
            console.log('❌ No longer searching, skipping auto-match');
            return;
          }

          // Pull from the same pool of 973 fake players on leaderboard
          const fakeOpponent = getRandomFakeOpponent(userProfile.elo_rating);
          const playerColor = Math.random() < 0.5 ? 'white' : 'black';
          
          console.log(`✅ Auto-match with fake player triggered: ${fakeOpponent.username} (${fakeOpponent.elo} ELO)`);
          
          setMatchFound({
            game_session_id: `BOT-${Date.now().toString(36).toUpperCase()}-ELO${fakeOpponent.elo}`,
            time_control: timeControl,
            player_color: playerColor,
            white_player_id: playerColor === 'white' ? userProfile.id : 'bot',
            black_player_id: playerColor === 'black' ? userProfile.id : 'bot',
            is_bot_game: true,
            bot_elo: fakeOpponent.elo,
            fakeOpponent: {
              username: fakeOpponent.username,
              elo: fakeOpponent.elo
            }
          });
          setIsSearching(false);
          setIsInQueue(false);
          isSearchingRef.current = false;
        }, delay);

        // THEN try to fetch queue data (optional, for display purposes)
        const { data: queueData } = await supabase
          .from('matchmaking_queue')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'waiting')
          .single();

        if (!isMountedRef.current) {
          return { type: 'joined_queue' };
        }

        if (queueData) {
          setQueueEntry(queueData as MatchmakingQueueEntry);
        }

        return { type: 'joined_queue' };
      }

      throw new Error('Unexpected matchmaking response');
    } catch (error: any) {
      if (!isMountedRef.current) {
        return false;
      }
      console.error('Error joining queue:', error);
      toast({
        title: "Error Joining Queue",
        description: error.message || "Failed to join matchmaking queue",
        variant: "destructive"
      });
      setIsSearching(false);
      return false;
    }
  }, [user, userProfile, toast, clearAutoMatchTimeout, eloTolerance]);

  // Offer bot match when no human opponent found (kept for manual triggers)
  const offerBotMatch = useCallback((timeControl: string) => {
    if (!userProfile) return;

    // Pull from the same pool of 973 fake players on leaderboard
    const fakeOpponent = getRandomFakeOpponent(userProfile.elo_rating);
    const playerColor = Math.random() < 0.5 ? 'white' : 'black';

    setIsSearching(false);
    setIsInQueue(false);
    setMatchFound({
      game_session_id: `BOT-${Date.now().toString(36).toUpperCase()}-ELO${fakeOpponent.elo}`,
      time_control: timeControl,
      player_color: playerColor,
      white_player_id: playerColor === 'white' ? userProfile.id : 'bot',
      black_player_id: playerColor === 'black' ? userProfile.id : 'bot',
      is_bot_game: true,
      bot_elo: fakeOpponent.elo,
      fakeOpponent: {
        username: fakeOpponent.username,
        elo: fakeOpponent.elo
      }
    });
  }, [userProfile]);

  // Leave matchmaking queue
  const leaveQueue = useCallback(async () => {
    if (!user) return;

    try {
      // Call cleanup function to remove ALL stale entries
      await supabase.rpc('cleanup_user_matchmaking_queue' as any, {
        _user_id: user.id
      });

      if (!isMountedRef.current) return;

      // Clear any pending auto-match timeout
      clearAutoMatchTimeout();
      isSearchingRef.current = false;
      setIsInQueue(false);
      setQueueEntry(null);
      setIsSearching(false);
      setTimeInQueue(0);
      setEloTolerance(100);
    } catch (error) {
      console.error('Error leaving queue:', error);
    }
  }, [user, clearAutoMatchTimeout]);

  // Set up secure polling for matches while in queue
  useEffect(() => {
    if (!user || !isInQueue || !queueEntry) return;

    const pollForMatch = async () => {
      try {
        const { data, error } = await supabase.rpc('poll_match_game_session', {
          _time_control: queueEntry.time_control,
          _tolerance: eloTolerance
        });

        if (!isMountedRef.current) return;

        if (error) throw error;

        const result = data?.[0] as any; // Cast to any since types haven't updated yet
        if (result && result.match_status === 'match_found' && result.game_session_id) {
          // Clear auto-match timeout since we found a real match
          clearAutoMatchTimeout();
          setMatchFound({
            game_session_id: result.game_session_id,
            time_control: result.matched_time_control,
            player_color: result.player_color,
            white_player_id: result.white_player_id,
            black_player_id: result.black_player_id
          });
          isSearchingRef.current = false;
          setIsInQueue(false);
          setIsSearching(false);
        }
      } catch (error) {
        if (!isMountedRef.current) return;
        console.error('Error polling for match:', error);
      }
    };

    // Poll every 2 seconds while in queue
    const interval = setInterval(pollForMatch, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [user, isInQueue, queueEntry, eloTolerance]);

  // Progressive ELO expansion while in queue
  useEffect(() => {
    if (!isInQueue) {
      setEloTolerance(100);
      return;
    }

    // Expand tolerance every 15 seconds
    const interval = setInterval(() => {
      if (!isMountedRef.current) return;
      setEloTolerance(prev => {
        const next = Math.min(prev + 50, 400);
        console.log(`📊 ELO tolerance expanded to ±${next}`);
        return next;
      });
    }, 15000);

    return () => clearInterval(interval);
  }, [isInQueue]);

  // Timer for queue time
  useEffect(() => {
    if (!isInQueue) return;

    const interval = setInterval(() => {
      if (!isMountedRef.current) return;
      setTimeInQueue(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isInQueue]);

  // Fetch queue stats periodically
  useEffect(() => {
    if (!isInQueue || !queueEntry) return;

    const fetchStats = async () => {
      const { data } = await supabase.rpc('get_queue_statistics' as any, {
        _time_control: queueEntry.time_control
      });
      if (!isMountedRef.current) return;
      if (data) {
        setQueueStats(data as any);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 10000);

    return () => clearInterval(interval);
  }, [isInQueue, queueEntry]);

  // Fetch profile on mount
  useEffect(() => {
    if (user) {
      fetchUserProfile();
    }
  }, [user, fetchUserProfile]);

  // Check for existing state on mount (rejoin mechanism)
  const checkExistingState = useCallback(async () => {
    if (!user) return null;

    try {
      // Check for active queue entry
      const { data: queueData } = await supabase
        .from('matchmaking_queue')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'waiting')
        .single();

      if (!isMountedRef.current) return null;

      if (queueData) {
        console.log('🔄 Found existing queue entry, rejoining...');
        setQueueEntry(queueData as MatchmakingQueueEntry);
        setIsInQueue(true);
        setIsSearching(true);
        return { type: 'queue', data: queueData };
      }

      // Check for active game session
      const { data: activeSessions } = await supabase
        .from('game_sessions')
        .select('*')
        .or(`white_player_id.eq.${user.id},black_player_id.eq.${user.id}`)
        .eq('game_status', 'ongoing')
        .order('created_at', { ascending: false })
        .limit(1);

      if (!isMountedRef.current) return null;

      if (activeSessions && activeSessions.length > 0) {
        const session = activeSessions[0];
        console.log('🎮 Found active game session, rejoining...');
        return { 
          type: 'active_game', 
          data: {
            game_session_id: session.id,
            time_control: session.time_control,
            player_color: session.white_player_id === user.id ? 'white' : 'black'
          }
        };
      }

      return null;
    } catch (error) {
      console.error('Error checking existing state:', error);
      return null;
    }
  }, [user]);

  // checkExistingState is exported for Index.tsx to handle rejoin logic

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Clear auto-match timeout on unmount to prevent state updates
      if (autoMatchTimeoutRef.current) {
        clearTimeout(autoMatchTimeoutRef.current);
      }
      if (user) {
        supabase.rpc('cleanup_user_matchmaking_queue' as any, { _user_id: user.id });
      }
    };
  }, [user]);

  return {
    userProfile,
    isInQueue,
    isSearching,
    matchFound,
    timeInQueue,
    eloTolerance,
    queueStats,
    joinQueue,
    leaveQueue,
    offerBotMatch,
    checkExistingState,
    fetchUserProfile
  };
}